import { component } from "picoapp";

declare function removeTrapFocus(
  elementToFocus?: HTMLElement
): void;

function getVisibleFocusables(container: HTMLElement): HTMLElement[] {
  const selector =
    "a[href], button:not([disabled]), input:not([type=hidden]):enabled, select:enabled, textarea:enabled, [tabindex]:not([tabindex^='-'])";
  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => el.offsetParent !== null
  );
}

/** After Tab on last focusable in a submenu: next main link, or first focusable after the main nav in header order. */
function focusNextTopLevelOrOut(
  header: HTMLElement,
  navUl: HTMLElement,
  currentTrigger: HTMLElement
): void {
  const topFocusables = Array.from(
    navUl.querySelectorAll<HTMLElement>(":scope > li > a, :scope > li > button")
  );
  const idx = topFocusables.indexOf(currentTrigger);
  if (idx >= 0 && idx < topFocusables.length - 1) {
    topFocusables[idx + 1].focus();
    return;
  }
  const ordered = getVisibleFocusables(header);
  const lastMain = topFocusables[topFocusables.length - 1];
  const i = ordered.indexOf(lastMain);
  if (i >= 0 && i < ordered.length - 1) {
    ordered[i + 1].focus();
  }
}

export const megaMenu = component((node: HTMLElement) => {
  const overlay = document.querySelector<HTMLElement>(".overlay");
  let lastActiveTrigger: HTMLElement | null = null;

  const openDropdown = (
    dropdownLink: HTMLElement,
    navDropdown: HTMLElement,
    overlay: HTMLElement,
    dropdownContainer: HTMLElement,
    node: HTMLElement,
    fromKeyboard = false
  ) => {
    const trigger = dropdownLink.firstElementChild as HTMLElement;
    trigger.setAttribute("aria-expanded", "true");
    node.querySelector(".header").classList.add("is-dropdown-open");
    node.classList.add("dropdown-active");
    overlay.classList.add("is-active");
    dropdownContainer
      .querySelector(".site-nav-dropdown__menu--active")
      .classList.remove("site-nav-dropdown__menu--active");

    navDropdown.classList.add("site-nav-dropdown__menu--active");

    dropdownContainer.classList.remove("tw-hidden");
    setTimeout(() => {
      dropdownContainer.classList.add("tw-opacity-100");
      const closeButton = navDropdown.querySelector<HTMLElement>(
        ".site-nav-dropdown-close"
      );
      const firstFocusable = navDropdown.querySelector<HTMLElement>(
        "a, button, input, [tabindex]:not([tabindex='-1'])"
      );

      const isKeyboardNavigation =
        fromKeyboard || trigger === document.activeElement;

      if (isKeyboardNavigation && firstFocusable) {
        lastActiveTrigger = trigger;
        firstFocusable.focus();
      }
      if (closeButton) {
        closeButton.onclick = () => {
          closeDropdown(
            dropdownLink,
            navDropdown,
            overlay,
            dropdownContainer,
            node
          );
        };
      }
    }, 0);
  };

  const closeDropdown = (
    dropdownLink: HTMLElement,
    navDropdown: HTMLElement,
    overlay: HTMLElement,
    dropdownContainer: HTMLElement,
    node: HTMLElement,
    options?: { force?: boolean; skipTriggerRefocus?: boolean }
  ) => {
    const force = options?.force ?? false;
    const skipTriggerRefocus = options?.skipTriggerRefocus ?? false;

    const canClose =
      force ||
      (!node.querySelector(".site-nav-dropdown:hover") &&
        !node.querySelector("#SiteNav li.site-nav--has-dropdown:hover"));

    if (!canClose) {
      return;
    }

    const trigger = dropdownLink.firstElementChild as HTMLElement;
    trigger.setAttribute("aria-expanded", "false");
    node.querySelector(".header").classList.remove("is-dropdown-open");
    node.classList.remove("dropdown-active");
    overlay.classList.remove("is-active");
    dropdownContainer
      .querySelector(".site-nav-dropdown__menu--active")
      .classList.remove("site-nav-dropdown__menu--active");
    navDropdown.classList.add("site-nav-dropdown__menu--active");
    dropdownContainer.classList.remove("tw-opacity-100");
    setTimeout(() => {
      dropdownContainer.classList.add("tw-hidden");
    }, 0);

    removeTrapFocus();

    if (!skipTriggerRefocus && lastActiveTrigger) {
      lastActiveTrigger.focus();
      lastActiveTrigger = null;
    } else if (skipTriggerRefocus) {
      lastActiveTrigger = null;
    }
  };

  const toggleInnerDropdown = (e: Event, innerDropdownButtons: NodeList) => {
    e.preventDefault();
    innerDropdownButtons.forEach((button: HTMLElement) => {
      const innerDropdownMenuClasslist = document.getElementById(
        button.getAttribute("data-handle")
      ).classList;
      const nestedButton = button.firstElementChild;

      if (e.target === nestedButton || e.target === button) {
        nestedButton.ariaExpanded = "true";
        button.classList.add("site-nav-dropdown__child-link--active");
        innerDropdownMenuClasslist.add("site-nav-dropdown__child-menu--active");
      } else {
        nestedButton.ariaExpanded = "false";
        button.classList.remove("site-nav-dropdown__child-link--active");
        innerDropdownMenuClasslist.remove(
          "site-nav-dropdown__child-menu--active"
        );
      }
    });
  };

  const handleInnerDropdownKeypress = (
    e: KeyboardEvent,
    innerDropdownButtons: NodeList
  ) => {
    const { code } = e;
    if (code !== "Tab") e.preventDefault();
    if (code === "Space" || code === "Enter") {
      toggleInnerDropdown(e, innerDropdownButtons);
    }
  };

  const handleKeypress = (
    dropdownLink: HTMLElement,
    e: KeyboardEvent,
    navDropdown: HTMLElement,
    overlay: HTMLElement,
    dropdownContainer: HTMLElement,
    node: HTMLElement
  ) => {
    const { code } = e;

    if (code === "Tab") {
      if (!e.shiftKey) {
        e.preventDefault();
        const trigger = dropdownLink.firstElementChild as HTMLElement;
        const isThisMenuActive =
          overlay.classList.contains("is-active") &&
          navDropdown.classList.contains("site-nav-dropdown__menu--active");

        if (isThisMenuActive) {
          const firstFocusable = navDropdown.querySelector<HTMLElement>(
            "a, button, input, [tabindex]:not([tabindex='-1'])"
          );
          if (firstFocusable) {
            lastActiveTrigger = trigger;
            firstFocusable.focus();
          }
        } else {
          openDropdown(
            dropdownLink,
            navDropdown,
            overlay,
            dropdownContainer,
            node,
            true
          );
        }
      }
      return;
    }

    if (code !== "Tab") e.preventDefault();
    const forceClose = code === "Escape";
    if (code === "Space" || code === "Enter" || forceClose) {
      overlay.classList.contains("is-active") || forceClose
        ? closeDropdown(
            dropdownLink,
            navDropdown,
            overlay,
            dropdownContainer,
            node
          )
        : openDropdown(
            dropdownLink,
            navDropdown,
            overlay,
            dropdownContainer,
            node
          );
    }
  };

  const initNav = (node: HTMLElement, overlay: HTMLElement) => {
    node
      .querySelectorAll<HTMLElement>(".site-nav > .site-nav--has-dropdown")
      .forEach((dropdownLink, idx) => {
        const dropdownContainer =
          node.querySelector<HTMLElement>(".site-nav-dropdown");
        const linkName = dropdownLink.getAttribute("data-child-list-handle");
        const navDropdown = node.querySelector<HTMLElement>(
          `.site-nav-dropdown__menu--${linkName}`
        );

        if (idx === 0) {
          navDropdown.classList.add("site-nav-dropdown__menu--active");
        }
        dropdownLink.firstElementChild.addEventListener(
          "keydown",
          (e: KeyboardEvent) =>
            handleKeypress(
              dropdownLink,
              e,
              navDropdown,
              overlay,
              dropdownContainer,
              node
            )
        );

        const handleSubmenuTabKeydown = (e: KeyboardEvent) => {
          if (e.key !== "Tab") return;
          if (!navDropdown.classList.contains("site-nav-dropdown__menu--active")) {
            return;
          }
          if (!overlay.classList.contains("is-active")) return;

          const focusables = getVisibleFocusables(navDropdown);
          if (!focusables.length) return;

          const active = document.activeElement as HTMLElement | null;
          if (!active) return;
          const activeIndex = focusables.indexOf(active);
          if (activeIndex === -1) return;

          const trigger = dropdownLink.firstElementChild as HTMLElement;

          if (e.shiftKey && activeIndex === 0) {
            e.preventDefault();
            lastActiveTrigger = null;
            closeDropdown(dropdownLink, navDropdown, overlay, dropdownContainer, node, {
              force: true,
              skipTriggerRefocus: true,
            });
            trigger.focus();
            return;
          }

          if (!e.shiftKey && activeIndex === focusables.length - 1) {
            e.preventDefault();
            lastActiveTrigger = null;
            closeDropdown(dropdownLink, navDropdown, overlay, dropdownContainer, node, {
              force: true,
              skipTriggerRefocus: true,
            });
            const navUl = node.querySelector<HTMLElement>("#SiteNav");
            if (navUl) {
              focusNextTopLevelOrOut(node, navUl, trigger);
            }
          }
        };

        navDropdown.addEventListener("keydown", handleSubmenuTabKeydown);

        dropdownLink.firstElementChild.addEventListener("mouseenter", () =>
          openDropdown(
            dropdownLink,
            navDropdown,
            overlay,
            dropdownContainer,
            node
          )
        );
        dropdownLink.addEventListener("mouseleave", () =>
          closeDropdown(
            dropdownLink,
            navDropdown,
            overlay,
            dropdownContainer,
            node
          )
        );
        navDropdown.addEventListener("mouseleave", () =>
          closeDropdown(
            dropdownLink,
            navDropdown,
            overlay,
            dropdownContainer,
            node
          )
        );

        const innerDropdownButtons = navDropdown.querySelectorAll(
          ".site-nav-dropdown__link-parent"
        );
        if (innerDropdownButtons.length) {
          innerDropdownButtons.forEach((button, idx) => {
            if (idx === 0) {
              button.classList.add("site-nav-dropdown__child-link--active");
              document
                .getElementById(button.getAttribute("data-handle"))
                .classList.add("site-nav-dropdown__child-menu--active");
            }

            button.addEventListener("keydown", (e: KeyboardEvent) =>
              handleInnerDropdownKeypress(e, innerDropdownButtons)
            );
            button.addEventListener("mouseenter", (e) =>
              toggleInnerDropdown(e, innerDropdownButtons)
            );
          });
        }

        return () => {
          dropdownLink.firstElementChild.removeEventListener(
            "keydown",
            (e: KeyboardEvent) =>
              handleKeypress(
                dropdownLink,
                e,
                navDropdown,
                overlay,
                dropdownContainer,
                node
              )
          );
          dropdownLink.firstElementChild.removeEventListener("mouseenter", () =>
            openDropdown(
              dropdownLink,
              navDropdown,
              overlay,
              dropdownContainer,
              node
            )
          );
          dropdownLink.removeEventListener("mouseleave", () =>
            closeDropdown(
              dropdownLink,
              navDropdown,
              overlay,
              dropdownContainer,
              node
            )
          );
          navDropdown.removeEventListener("mouseleave", () =>
            closeDropdown(
              dropdownLink,
              navDropdown,
              overlay,
              dropdownContainer,
              node
            )
          );
          innerDropdownButtons.forEach((button) => {
            button.removeEventListener("keydown", (e: KeyboardEvent) =>
              toggleInnerDropdown(e, innerDropdownButtons)
            );
          });
        };
      });
  };

  initNav(node, overlay);
});
